import { defineQuery, enterQuery, exitQuery } from 'bitecs';
import { Animal, Attack, Combatant, CombatModeState, ConstructionSite, Corpse, DetailedHealth, Health, Mood, Needs, NetworkEntity, NpcNeeds, PathFollower, Pawn, PawnAI, Player, Position, Projectile, Task, Velocity, } from './components.js';
// ============================================================================
// Pawn Queries
// ============================================================================
// All pawns in the world
export const pawnQuery = defineQuery([Pawn, Position, NetworkEntity]);
// Pawns that are actively following a path
export const movingPawnsQuery = defineQuery([Pawn, Position, Velocity, PathFollower]);
// Pawns with an active task
export const workingPawnsQuery = defineQuery([Pawn, Position, Task]);
// Pawns with needs (for needs decay system)
export const pawnNeedsQuery = defineQuery([Pawn, Needs]);
// Pawns with health (for damage/healing systems)
export const pawnHealthQuery = defineQuery([Pawn, Health]);
// Pawns with AI enabled (for autonomous behavior system)
export const pawnAIQuery = defineQuery([Pawn, PawnAI, Position, Needs, NetworkEntity]);
// ============================================================================
// Player Queries
// ============================================================================
// All connected players
export const playerQuery = defineQuery([Player, NetworkEntity]);
// ============================================================================
// Network Queries
// ============================================================================
// All networked entities (for snapshot generation)
export const networkEntityQuery = defineQuery([NetworkEntity, Position]);
// ============================================================================
// Enter/Exit Queries (for detecting when entities gain/lose components)
// ============================================================================
// Detect when pawns start/stop moving
export const pawnStartedMovingQuery = enterQuery(movingPawnsQuery);
export const pawnStoppedMovingQuery = exitQuery(movingPawnsQuery);
// Detect when pawns start/stop working
export const pawnStartedWorkingQuery = enterQuery(workingPawnsQuery);
export const pawnStoppedWorkingQuery = exitQuery(workingPawnsQuery);
// ============================================================================
// Construction Queries
// ============================================================================
// All construction sites
export const constructionSiteQuery = defineQuery([ConstructionSite, NetworkEntity]);
// Construction sites in progress (being actively worked on)
export const activeConstructionQuery = defineQuery([ConstructionSite, NetworkEntity]);
// ============================================================================
// Animal Queries
// ============================================================================
// All animals in the world
export const npcQuery = defineQuery([Animal, Position, NetworkEntity]);
// Animals with needs (for AI behavior)
export const npcNeedsQuery = defineQuery([Animal, NpcNeeds]);
// Animals that are moving
export const movingAnimalsQuery = defineQuery([Animal, Position, Velocity, PathFollower]);
// Animals in combat
export const combatantAnimalQuery = defineQuery([Animal, Combatant]);
// ============================================================================
// Health System Queries
// ============================================================================
// Entities with detailed health (pawns and animals)
export const detailedHealthQuery = defineQuery([DetailedHealth, Position, NetworkEntity]);
// Entities with mood (humanoids)
export const moodQuery = defineQuery([Mood, DetailedHealth]);
// Entities currently bleeding
export const bleedingQuery = defineQuery([DetailedHealth, Position]);
// ============================================================================
// Combat Queries
// ============================================================================
// All combatants (pawns and animals)
export const combatantQuery = defineQuery([Combatant, Position, NetworkEntity]);
// Entities currently attacking
export const attackingQuery = defineQuery([Attack, Combatant, Position]);
// Corpses in the world
export const corpseQuery = defineQuery([Corpse, Position, NetworkEntity]);
// ============================================================================
// Enter/Exit Queries - Animals
// ============================================================================
// Detect when animals spawn/despawn
export const animalSpawnedQuery = enterQuery(npcQuery);
export const animalDespawnedQuery = exitQuery(npcQuery);
// Detect when animals start/stop moving
export const animalStartedMovingQuery = enterQuery(movingAnimalsQuery);
export const animalStoppedMovingQuery = exitQuery(movingAnimalsQuery);
// Detect when entities die
export const entityDiedQuery = enterQuery(corpseQuery);
// ============================================================================
// Combat Mode Queries
// ============================================================================
// All pawns with combat mode enabled
export const pawnCombatModeQuery = defineQuery([Pawn, CombatModeState, Position, NetworkEntity]);
// Pawns actively targeting something
export const activeCombatQuery = defineQuery([Pawn, CombatModeState, Position, Health]);
// Animals and pawns that can participate in combat
export const combatParticipantQuery = defineQuery([Position, Health, NetworkEntity]);
// ============================================================================
// Projectile Queries
// ============================================================================
// All projectiles in flight
export const projectileQuery = defineQuery([Projectile, Position, Velocity, NetworkEntity]);
// Detect when projectiles spawn/despawn
export const projectileSpawnedQuery = enterQuery(projectileQuery);
export const projectileDespawnedQuery = exitQuery(projectileQuery);
//# sourceMappingURL=queries.js.map